import React, { useMemo, useRef } from "react";
import classes from "../TransInter.module.scss";
import {
  Box,
  Button,
  Flex,
  Select,
  Space,
  Stack,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconChevronDown,
  IconInfoCircle,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useTransFormContext } from "../TransInterFormContext";
import { TransInterStepThreeProps, IFormTransDocument } from "../types";
import { useGetIntlTransferPurposeQuery } from "@/services/internationalTransferAPI";
import DocumentChecklistModal, {
  getRequiredDocuments,
} from "../components/DocumentChecklistModal";
import FileBadge from "../components/FileBadge";

// Icon lịch trắng, không có ký hiệu bên trong (khớp design).
const CalendarBlankIcon = ({
  size = 18,
  color = "#6B7280",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z" />
    <path d="M16 3v4" />
    <path d="M8 3v4" />
    <path d="M4 11h16" />
  </svg>
);


const TransInterStepThree: React.FC<TransInterStepThreeProps> = ({
  onSubmit,
  onBackStep,
}) => {
  const { t, i18n } = useTranslation();
  const form = useTransFormContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [checklistOpened, { open: openChecklist, close: closeChecklist }] =
    useDisclosure(false);

  const { data: purposeData_ } = useGetIntlTransferPurposeQuery();
  const purposeGroups = useMemo(
    () => purposeData_?.body?.purposeGroups || [],
    [purposeData_],
  );

  // Chọn tên theo ngôn ngữ hiện tại (name = tiếng Việt, nameEn = tiếng Anh).
  const pickName = (o: { name: string; nameEn?: string }) =>
    (i18n.language || "vi").startsWith("en") && o.nameEn ? o.nameEn : o.name;

  const purposeData = purposeGroups.map((g) => ({
    value: g.code,
    label: pickName(g),
  }));
  const purposeDetailData = useMemo(() => {
    const parent = purposeGroups.find((g) => g.code === form.values.purposeType);
    return (parent?.items || []).map((it) => ({
      value: it.code,
      label: pickName(it),
    }));
  }, [purposeGroups, form.values.purposeType, i18n.language]);

  // Hồ sơ yêu cầu = HỒ SƠ BẮT BUỘC theo mục đích chi tiết đang chọn (lấy từ API getIntlTransferPurpose).
  const requiredDocuments = useMemo(
    () => getRequiredDocuments(purposeGroups, form.values.purposeDetail),
    [purposeGroups, form.values.purposeDetail],
  );

  const documents = form.values.documents || [];

  const handleFileSelected = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = (reader.result as string) || "";
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      const ext = file.name.split(".").pop() || "";
      const newDoc: IFormTransDocument = {
        documentType: "OTHER",
        documentName: file.name,
        fileType: ext,
        fileContent: base64,
      };
      form.setFieldValue("documents", [...documents, newDoc]);
    };
    reader.readAsDataURL(file);
    // reset để chọn lại cùng file vẫn kích hoạt onChange
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveDocument = (index: number) => {
    form.setFieldValue(
      "documents",
      documents.filter((_, i) => i !== index),
    );
  };

  const handleNext = () => {
    const r1 = form.validateField("purposeType");
    const r2 = form.validateField("purposeDetail");
    if (!r1.hasError && !r2.hasError) onSubmit?.();
  };

  return (
    <>
      <Box className={classes.sourceAccount} w="65%">
        <Flex justify="space-between" gap={20}>
          <Select
            data={purposeData}
            w="50%"
            label={`${t("PURPOSE_TYPE")}`}
            placeholder={`${t("PURPOSE_TYPE")}`}
            rightSection={<IconChevronDown width={16} />}
            radius={8}
            value={form.values.purposeType || ""}
            error={form.errors.purposeType}
            onChange={(value) => {
              form.setFieldValue("purposeType", value || "");
              form.setFieldValue("purposeDetail", "");
            }}
          />
          <Select
            data={purposeDetailData}
            w="50%"
            label={`${t("PURPOSE_OF_MONEY_TRANSFER")}`}
            placeholder={`${t("PURPOSE_OF_MONEY_TRANSFER")}`}
            rightSection={<IconChevronDown width={16} />}
            radius={8}
            value={form.values.purposeDetail || ""}
            error={form.errors.purposeDetail}
            onChange={(value) => form.setFieldValue("purposeDetail", value || "")}
          />
        </Flex>

        <Flex gap={6} mt={8} align="center">
          <IconInfoCircle color="#6B7280" size={16} />
          <Text fz={14} c="#6B7280">
            {t("PURPOSE_DOC_CHECKLIST")}
          </Text>
          <Text
            fz={14}
            fw={600}
            c="#1E88E5"
            style={{ cursor: "pointer" }}
            onClick={openChecklist}
          >
            {t("HERE")}
          </Text>
        </Flex>

        <Space h={20} />
        <Text fw={600} fz={16} c="#212936">
          {t("REQUIRED_DOCUMENTS")}
        </Text>
        <Stack spacing={6} mt={8}>
          {requiredDocuments.map((doc) => (
            <Box
              key={doc.code || doc.name}
              style={{ display: "flex", gap: 8, alignItems: "flex-start" }}
            >
              <Box
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: "#374151",
                  marginTop: 8,
                  flexShrink: 0,
                }}
              />
              <Text fz={14} c="#374151">
                {pickName(doc)}
              </Text>
            </Box>
          ))}
        </Stack>

        <Space h={16} />
        <Stack spacing={10}>
          {documents.map((doc, index) => (
            <Flex
              key={`${doc.documentName}-${doc.fileContent.slice(0, 12)}`}
              justify="space-between"
              align="center"
            >
              <Flex gap={10} align="center">
                <FileBadge fileType={doc.fileType} />
                <Text fz={14} c="#157CC1">
                  {doc.documentName}
                </Text>
              </Flex>
              <Box
                style={{ cursor: "pointer" }}
                onClick={() => handleRemoveDocument(index)}
              >
                <IconTrash size={20} color="#F1533E" />
              </Box>
            </Flex>
          ))}
        </Stack>

        <Space h={16} />
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={(e) => handleFileSelected(e.currentTarget.files?.[0] ?? null)}
        />
        <Box
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: "1px dashed #2A81D0",
            borderRadius: 10,
            padding: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            cursor: "pointer",
            color: "#2A81D0",
          }}
        >
          <IconPlus size={18} />
          <Text fz={14} fw={600} c="#2A81D0">
            {t("DOWNLOAD_MORE_DOCUMENTS")}
          </Text>
        </Box>
      </Box>

      <Space h={20} />

      {/* Footer cố định */}
      <Box
        style={{
          position: "fixed",
          bottom: 0,
          left: "calc(var(--mantine-navbar-width, 0px) + 20px)",
          right: 0,
          padding: "10px 24px",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "10px",
          boxShadow: "0px 5px 15px 0px #00000026",
          borderRadius: "12px",
          height: "76px",
          backgroundColor: "white",
          zIndex: 1,
        }}
      >
        <Flex gap={10}>
          <Button
            onClick={onBackStep}
            sx={() => ({
              width: "100px",
              backgroundColor: "#F4F4F6",
              color: "#525866",
              borderRadius: "8px",
              padding: "8px 10px",
              fontWeight: 400,
              "&:hover": { backgroundColor: "#F4F4F6" },
            })}
          >
            {t("BACK")}
          </Button>
          <Button
            type="submit"
            onClick={handleNext}
            sx={() => ({
              width: "160px",
              backgroundColor: "#2A81D0",
              color: "white",
              borderRadius: "8px",
              padding: "8px 10px",
              fontWeight: 400,
            })}
          >
            {t("NEXT")}
          </Button>
        </Flex>
      </Box>

      {/* chừa khoảng cho footer cố định */}
      <Space h={80} />

      <DocumentChecklistModal
        opened={checklistOpened}
        onClose={closeChecklist}
        purposeGroups={purposeGroups}
      />
    </>
  );
};

export default TransInterStepThree;
